import type { Shape, Storey } from '@shared/models/design/storey.model';
import type { MapSelections } from '../models/map-observables.models';
import type { BuildingDesign } from '@shared/models/design/building-design.model';
import {
  getBuildingById,
  getStoreyElevation,
  getBuildingStoreys,
  getOtherBuildings,
} from '@shared/helpers/design/storey.helpers';

/**
 * Whether a missing {@link selectedStoreyId} should snap to the ground storey.
 *
 * Keep a newly chosen id until it appears on the next storey list (optimistic
 * add). Reset only when the id used to be present and has disappeared (delete /
 * building switch). An absent id is an intentional "no storey selected" state.
 */
export const shouldResetMissingSelectedStoreyId = (
  storeys: readonly { id: string }[],
  selectedStoreyId: string | undefined,
  previousStoreys: readonly { id: string }[] | undefined,
): boolean => {
  if (storeys.some((storey) => storey.id === selectedStoreyId)) return false;
  if (!selectedStoreyId) return false;
  return Boolean(
    previousStoreys?.some((storey) => storey.id === selectedStoreyId),
  );
};

/** A building opens on its bottom storey, independent of the current camera. */
export const getBuildingOpenStoreyId = (
  storeys: readonly { id: string }[],
): string | undefined => storeys[0]?.id;

/** Only visibly selected buildings expose editable storeys to map picking. */
export const getDesignMapBuildingGeometry = (
  design: BuildingDesign,
  selectedBuildingId: string | undefined,
  hasBuildingSelection: boolean,
) => ({
  selectedBuildingId: hasBuildingSelection
    ? getBuildingById(design, selectedBuildingId)?.id
    : undefined,
  storeys: hasBuildingSelection
    ? getBuildingStoreys(design, selectedBuildingId)
    : [],
  otherBuildings: hasBuildingSelection
    ? getOtherBuildings(design, selectedBuildingId)
    : design.buildings,
});

/** Whole-shape selection and an explicit selection of every vertex both select the polygon. */
export const isEntirePolygonSelected = (
  shape: Shape | undefined,
  selection: Pick<MapSelections, 'shapeId' | 'pointIndexes'>,
): boolean => {
  if (!shape || selection.shapeId !== shape.id) return false;
  const indexes = selection.pointIndexes;
  if (!indexes?.length) return true;
  return shape.points.every((_point, index) => indexes.includes(index));
};

/** Isolate an unselected hovered floor while preserving its original stack elevation. */
export const getHoveredStoreyForOutline = (
  storeys: Storey[],
  hoveredStoreyId: string | undefined,
  selectedStoreyId: string | undefined,
): Storey | undefined => {
  if (!hoveredStoreyId || hoveredStoreyId === selectedStoreyId)
    return undefined;
  const storey = storeys.find(({ id }) => id === hoveredStoreyId);
  return storey
    ? { ...storey, elevation: getStoreyElevation(storeys, storey) }
    : undefined;
};
